import { formatMoney } from '../helpers'
import slugify from 'slugify'
import { quantityIncrementor } from './misc'
import {
  templateCustomerDiscountItem,
  templateDiscountItem,
} from './cart-discounts'

const cartItemImage = async item => `
  <a
    href="${item.url}"
    title="${item.product_title}"
    class="cart-item-product__image"
  >
    <figure class="cart-item-product__image-figure">
      ${
        item.image
          ? `<img class="cart-item-product__image-img" src="${item.image}" sizes="7em" alt="${item.product_title}">`
          : ''
      }
    </figure>
  </a>
`

const cartItemTitle = async item => `
  <a href="${item.url}" class="cart-item-product__info-title" data-cart-item-title data-role="product-title">
    ${item.product_title}
  </a>
`

const cartItemVariant = async item => {
  if (
    item.options_with_values.length === 0 ||
    (item.options_with_values.length === 1 &&
      item.options_with_values[0].name === 'Title')
  ) {
    return ''
  }

  return item.options_with_values
    .map(({ name, value }) => {
      const optionClassName = slugify(name)
      const valueClassName = slugify(value)

      return `
        <div class="cart-item-product__info-variant">
          <span class="cart-item-product__info-variant-name cart-item-product__info-variant-name--${optionClassName}">
            ${name}:
          </span>

          <span class="cart-item-product__info-variant-value cart-item-product__info-variant-value--${valueClassName}">
            ${value}
          </span>
        </div>
      `
    })
    .join('\n')
}

const cartItemProperties = async item => {
  const properties = Object.entries(item.properties)
  if (properties.length === 0) {
    return ''
  }

  return properties
    .map(([name, value]) => {
      const optionClassName = slugify(name)
      const valueClassName = slugify(value)

      if (!value || value === '') {
        return ''
      }

      return `
      <div class="cart-item-product__info-property">
          <span class="cart-item-product__info-property-name cart-item-product__info-property-name--${optionClassName}">
            ${name}:
          </span>

          <span class="cart-item-product__info-property-value cart-item-product__info-property-value--${valueClassName}">
            ${value}
          </span>
        </div>
        `
    })
    .join('\n')
}

const cartItemProduct = async (item, lineId) => `
  <div class="cart-item-product">
    ${item.image ? await cartItemImage(item) : ''}

    <div class="cart-item-product__info">
      ${await cartItemTitle(item)}
      ${await cartItemVariant(item)}
      ${await cartItemProperties(item)}
      ${await cartItemSellingPlans(item, lineId)}
      ${await cartItemRemoveButton(item, lineId)}
    </div>
  </div>
`

const cartItemSellingPlans = async (item, lineId) => {
  if (!item.sellingPlanGroups) {
    return ''
  }

  const currentPlanId =
    item.selling_plan_allocation?.selling_plan?.id.toString()

  return item.sellingPlanGroups
    .map(
      group => `
    <div class="mini-cart-item__info-plan-group">
      <label class="mini-cart-item__info-plan-group-label">
        <input data-selling-plan-toggle type="checkbox" class="mini-cart-item__info-plan-group-toggle" data-line-id="${lineId}" ${group.sellingPlans.find(plan => plan.id === currentPlanId) !== undefined ? 'checked' : ''} />
        <h5 class="mini-cart-item__info-plan-group-title">${group.name}</h5>
      </label>

      ${
        group.sellingPlans.length > 1
          ? `
              <select data-selling-plan-select data-line-id="${lineId}" class="mini-cart-item__info-plan-select">
                ${group.sellingPlans
                  .map(
                    plan => `
                  <option value="${plan.id}" ${plan.id === currentPlanId ? 'selected' : ''}>${plan.name}</option>
                `,
                  )
                  .join('')}
              </select>
            `
          : `
              <input type="hidden" data-selling-plan-select data-line-id="${lineId}" value="${group.sellingPlans[0].id}" />
              <span class="mini-cart-item__info-plan-name">${group.sellingPlans[0].name}</span>
            `
      }
    </div>
  `,
    )
    .join('')
}

const cartItemPrice = async item => `
  <div class="cart-item-product__info-price" data-cart-item-price>
    ${item.final_price !== item.original_price ? `<s class="cart-item-product__info-price-original">${formatMoney(item.original_price)}</s>` : ''}
    ${formatMoney(item.final_price)}
  </div>
`

const cartItemTotal = async item => `
  <div class="cart-item-product__total" data-cart-item-total>
    ${formatMoney(item.final_line_price)}
  </div>
`
const cartItemDiscounts = async item => `
  <ul class="mini-cart-item__info-discounts">
    ${(
      await Promise.all(
        item.line_level_discount_allocations.map(
          ({ amount, discount_application }) => {
            const data = {
              key: discount_application.key,
              title: discount_application.title,
              amount,
            }

            return discount_application.type === 'discount_code'
              ? templateCustomerDiscountItem(data)
              : templateDiscountItem(data)
          },
        ),
      )
    ).join('')}
  </ul>
`

const cartItemRemoveButton = async (item, lineId) => `
  <a
    href="/cart/change?line=${lineId}&amp;quantity=0"
    class="cart-item-product__info-remove cart-remove"
    aria-label="${window.theme.strings.cart.label.remove}"
    data-remove-line-item
    data-line-id="${lineId}"
  >
    <span class="screenreader-text">${window.theme.strings.cart.label.remove}</span>
    <svg width="16" height="18" viewBox="0 0 16 18" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M1 5.17285H14.9442" stroke="var(--foreground)" stroke-width="2" stroke-miterlimit="10" stroke-linecap="square" stroke-linejoin="round"/>
      <path d="M5.23438 4.67479V3.00358C5.23423 2.80608 5.26697 2.61049 5.33072 2.42799C5.39447 2.24549 5.48797 2.07967 5.60589 1.94002C5.7238 1.80036 5.8638 1.68962 6.01788 1.61411C6.17197 1.53861 6.33711 1.49983 6.50386 1.5H9.2562C9.42295 1.49983 9.5881 1.53861 9.74218 1.61411C9.89627 1.68962 10.0363 1.80036 10.1542 1.94002C10.2721 2.07967 10.3656 2.24549 10.4293 2.42799C10.4931 2.61049 10.5258 2.80608 10.5257 3.00358V4.67479" stroke="var(--foreground)" stroke-width="2" stroke-miterlimit="10"/>
      <path d="M6.29297 8.90771L6.29297 12.0825" stroke="var(--foreground)" stroke-width="2" stroke-miterlimit="10" stroke-linecap="square" stroke-linejoin="round"/>
      <path d="M9.46875 8.90771L9.46875 12.0825" stroke="var(--foreground)" stroke-width="2" stroke-miterlimit="10" stroke-linecap="square" stroke-linejoin="round"/>
      <path d="M2.05859 4.6748L2.95383 14.6783C3.04613 15.7096 3.91038 16.5 4.94586 16.5H11.1246C12.184 16.5 13.0597 15.6738 13.1212 14.6162L13.6995 4.6748" stroke="var(--foreground)" stroke-width="2"/>
    </svg>
  </a>
`

export const templateCartItem = async (item, lineId) => `
  <tr class="responsive-table-row cart-table__product-row" data-product="${item.id}" data-line-id="${lineId}">
    <td data-label="${window.theme.strings.cart.label.product}">
      ${await cartItemProduct(item, lineId)}
    </td>

    <td data-label="${window.theme.strings.cart.label.price}">
      ${await cartItemPrice(item)}
      ${await cartItemDiscounts(item)}
    </td>

    <td data-label="${window.theme.strings.cart.label.quantity}">
      ${await quantityIncrementor(item.quantity, item.variant_id, lineId, 0, item.variant_metafields?.product?.quantity_increment || 1)}
    </td>

    <td data-label="${window.theme.strings.cart.label.total}">
      ${await cartItemTotal(item)}
    </td>
  </tr>
`
